import { useState, useEffect, useMemo, useCallback } from "react";
import { AlertTriangle, TrendingUp, TrendingDown, CloudLightning } from "lucide-react";
import { getAllSeasons } from "@/lib/stormsQueries";
import StormAccumulationChart from "@/components/StormAccumulationChart";
import { supabase } from "@/lib/supabaseClient";

function StatCard({ label, value, sub, icon: Icon, color = "text-cyan-400" }) {
  return (
    <div className="border border-white/10 bg-[#111827] p-5">
      {Icon && (
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`w-4 h-4 ${color}`} />
          <p className="text-xs uppercase tracking-widest text-[#6B7280]">{label}</p>
        </div>
      )}
      {!Icon && <p className="text-xs uppercase tracking-widest text-[#6B7280] mb-2">{label}</p>}
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-[#6B7280] mt-1">{sub}</p>}
    </div>
  );
}

export default function SummaryStatsPage() {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSeasons = useCallback(() => {
    getAllSeasons()
      .then(setSeasons)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  // Auto-refresh when the named_storms table changes (e.g. a new storm is added)
  useEffect(() => {
    const channel = supabase
      .channel("summary-stats-named-storms")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "named_storms" },
        loadSeasons
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [loadSeasons]);

  const stats = useMemo(() => {
    if (!seasons.length) return null;

    const count = (s) => Number(s.storm_count) || 0;
    const totalStorms = seasons.reduce((acc, s) => acc + count(s), 0);
    const activeSeasonsOnly = seasons.filter((s) => count(s) > 0);

    const mostActive = activeSeasonsOnly.length
      ? activeSeasonsOnly.reduce((best, s) =>
          count(s) > count(best) ? s : best, activeSeasonsOnly[0])
      : null;

    const leastActive = activeSeasonsOnly.length
      ? activeSeasonsOnly.reduce((best, s) =>
          count(s) < count(best) ? s : best, activeSeasonsOnly[0])
      : null;

    return { totalStorms, mostActive, leastActive };
  }, [seasons]);

  if (error) {
    return (
      <div className="border border-red-900/40 bg-[#1a0a0a] p-4 flex items-start gap-3 text-red-400">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <p className="text-sm">Failed to load summary stats: {error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-28 bg-[#111827] border border-white/10" />
          ))}
        </div>
        <div className="h-48 bg-[#111827] border border-white/10" />
      </div>
    );
  }

  if (!stats || seasons.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 text-[#6B7280]">
        <CloudLightning className="w-10 h-10 opacity-30" />
        <p className="text-sm">No storm data available yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#F9FAFB]">Summary Statistics</h2>
        <p className="text-sm text-[#6B7280] mt-1">All-time statistics across all seasons</p>
      </div>

      {/* Top stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Total Storms"
          value={stats.totalStorms}
          sub={`across ${seasons.length} seasons`}
          color="text-cyan-400"
        />
        <StatCard
          label="Most Active Season"
          value={stats.mostActive ? stats.mostActive.storm_count : "—"}
          sub={stats.mostActive ? `${stats.mostActive.season} season` : undefined}
          icon={TrendingUp}
          color="text-cyan-400"
        />
        <StatCard
          label="Least Active Season"
          value={stats.leastActive ? stats.leastActive.storm_count : "—"}
          sub={stats.leastActive ? `${stats.leastActive.season} season` : undefined}
          icon={TrendingDown}
          color="text-blue-400"
        />
      </div>

      {/* Storm accumulation chart */}
      <StormAccumulationChart />

      {/* Season-by-season storm names */}
      <div className="border border-white/10 bg-[#111827] overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#F9FAFB]">Storm Names by Season</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">Historical record of all named storms</p>
        </div>
        <div className="divide-y divide-white/5">
          {seasons.map((s) => (
            <div key={s.season} className="px-4 py-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-cyan-400">{s.season}</span>
                <span className="text-xs text-[#6B7280]">({s.storm_count ?? 0} storms)</span>
              </div>
              {s.storm_names && s.storm_names.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {s.storm_names.map((name) => (
                    <span
                      key={name}
                      className="px-2 py-0.5 text-xs font-medium bg-white/5 text-[#9CA3AF] border border-white/10"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#6B7280]">No storms this season</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
